/**
 * Column addition and subtraction of any size, with the misconceptions read digit by digit.
 * The books' words: "Ones first, then tens, then hundreds. When a column makes ten or more, rename."
 * "If you don't have enough ones — go get a 10!" (L2.13; never "borrow", never "carry").
 */
import { int } from "../core";
import type { Item, Skill } from "../types";

const digits = (n: number, w: number) => String(n).padStart(w, "0").split("").map(Number);

/** What the student gets if every column is added but no ten is ever traded (347 + 285 → 522). */
export function noTradeSum(a: number, b: number): number {
  const w = Math.max(String(a).length, String(b).length);
  const A = digits(a, w), B = digits(b, w);
  return Number(A.map((d, i) => (d + B[i]!) % 10).join(""));
}
/** Each column's full sum written side by side (47 + 38 → 715). */
export function columnsWritten(a: number, b: number): number {
  const w = Math.max(String(a).length, String(b).length);
  const A = digits(a, w), B = digits(b, w);
  return Number(A.map((d, i) => d + B[i]!).join(""));
}
/** The smaller digit taken from the larger in every column (523 − 247 → 324). */
export function smallerFromLarger(a: number, b: number): number {
  const w = String(a).length;
  const A = digits(a, w), B = digits(b, w);
  return Number(A.map((d, i) => Math.abs(d - B[i]!)).join(""));
}

const hit = (raw: string, n: number) => raw.trim().replace(/,/g, "") === String(n);

export function addItem(a: number, b: number, skill: string): Item {
  const ans = a + b;
  const w = Math.max(String(a).length, String(b).length);
  const A = digits(a, w), B = digits(b, w);
  const onesSum = A[w - 1]! + B[w - 1]!;
  return {
    id: `${skill}:${a}+${b}`,
    skill,
    prompt: "Add. Rename when you need to.",
    input: { kind: "vertical", top: a, bottom: b, op: "+" },
    speak: `Add ${a} plus ${b}.`,
    answer: String(ans),
    diagnose: (raw) => {
      const nt = noTradeSum(a, b);
      if (nt !== ans && hit(raw, nt)) return { code: "add-lost-ten", hint: "A column made ten or more. Did the new ten go in the box above the next column — and get added in?" };
      const cw = columnsWritten(a, b);
      if (cw !== ans && hit(raw, cw)) return { code: "add-two-digits-one-place", hint: "A place only holds one digit. Ten ones trade for a ten — where does that ten go?" };
      if (hit(raw, ans - 10) || hit(raw, ans - 100)) return { code: "add-lost-ten", hint: "One of the new tens didn't get added. Check the boxes above the columns." };
      return null;
    },
    hints: [
      `Ones first: ${A[w - 1]} + ${B[w - 1]}.`,
      onesSum >= 10 ? `${onesSum} ones is a ten and ${onesSum - 10} ones. Write ${onesSum - 10}; the new ten goes in the box above the tens. Then add the tens — with the new one.` : "Then the tens, then the hundreds. When a column makes ten or more, rename.",
      `Column by column, ones first, renaming each ten: ${a} + ${b} = ${ans}.`,
    ],
  };
}

export function subItem(a: number, b: number, skill: string): Item {
  const ans = a - b;
  const w = String(a).length;
  const A = digits(a, w), B = digits(b, w);
  return {
    id: `${skill}:${a}-${b}`,
    skill,
    prompt: "Subtract. Rename when you need to.",
    input: { kind: "vertical", top: a, bottom: b, op: "−" },
    speak: `Subtract ${a} minus ${b}.`,
    answer: String(ans),
    diagnose: (raw) => {
      const sl = smallerFromLarger(a, b);
      if (sl !== ans && hit(raw, sl)) return { code: "sub-smaller-from-larger", hint: "Look at each column from the top: are there enough to take away? If not, go get a ten." };
      if (hit(raw, ans + 10) || hit(raw, ans + 100) || hit(raw, ans + 110)) return { code: "sub-kept-ten", hint: "You took a ten to make more. Did you cross it off and write the smaller number above?" };
      return null;
    },
    hints: [
      `Ones first: ${A[w - 1]} − ${B[w - 1]}. Are there enough ones?`,
      A[w - 2] === 0 ? `There are no tens to get a ten from. Go get one from the hundreds first: it becomes 10 tens. Then take one of those tens for the ones.` : "Not enough? Tap the digit you go get a ten from. It is crossed off, the smaller number goes above, and the ten ones join the column.",
      `Column by column, ones first, getting a ten when you need one: ${a} − ${b} = ${ans}.`,
    ],
  };
}

/** Three-digit addition: renaming in the ones and/or the tens, sum under 1000 (L3.2: 347 + 285, 608 + 195). */
export const add3Vertical: Skill = {
  id: "add-3digit-vertical",
  name: "Add 3-digit numbers — rename when you need to",
  gen: (rng, level) => {
    let a = 0, b = 0;
    for (;;) {
      a = int(rng, 105, 689); b = int(rng, 105, 399);
      const ones = (a % 10) + (b % 10) >= 10, tens = Math.floor(a / 10) % 10 + Math.floor(b / 10) % 10 + (ones ? 1 : 0) >= 10;
      if (a + b < 1000 && (ones || tens) && (level < 2 || (ones && tens))) break;
    }
    return addItem(a, b, "add-3digit-vertical");
  },
};

/** Three-digit subtraction: renaming in one or two places, including across a zero (700 − 356). */
export const sub3Vertical: Skill = {
  id: "sub-3digit-vertical",
  name: "Subtract 3-digit numbers — rename when you need to",
  gen: (rng, level) => {
    let a = 0, b = 0;
    if (level >= 2 && rng() < 0.5) {
      a = int(rng, 3, 9) * 100 + (rng() < 0.5 ? 0 : int(rng, 1, 9));
      b = int(rng, 101, a - 50);
      return subItem(a, b, "sub-3digit-vertical");
    }
    for (;;) {
      a = int(rng, 312, 989); b = int(rng, 105, a - 40);
      const needOnes = a % 10 < b % 10, needTens = Math.floor(a / 10) % 10 - (needOnes ? 1 : 0) < Math.floor(b / 10) % 10;
      if (needOnes || needTens) break;
    }
    return subItem(a, b, "sub-3digit-vertical");
  },
};
